import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowUp, Mail, Facebook } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BrandMark } from '@/components/layout/BrandMark';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '@/lib/contact';
import { editorLang, editorPath } from '@/components/editorPages/routes';
import { useAvatarEntry } from '@/components/facelock/useAvatarEntry';
import { STUDIOS } from '@/lib/studios';
import quickTools from '@/components/quicktools/tools.json';

// Footer (immersive redesign, 2026-09-24). Brand + description + contact on
// the left, the two link groups beside it (two columns on phones), then the
// legal line and a back-to-top button, then an oversized, faint FACESHOT
// wordmark that rises into place as the page reaches its end. The wordmark
// is decoration only (aria-hidden); the logo above carries the name.

// Phones: 44px rows; a long Spanish label ("Política de reembolsos") wraps
// onto a second line inside its row instead of widening the column.
const LINK_CLASS =
  'link-underline inline-flex min-h-[44px] min-w-[44px] items-center py-1.5 text-[15px] leading-snug text-muted-foreground transition-colors duration-300 hover:text-foreground md:min-h-0 md:text-[0.9rem] [--underline-bottom:0.3rem] md:[--underline-bottom:0.2rem]';

const ICON_CLASS =
  'flex h-[44px] w-[44px] items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-muted-foreground transition-[color,border-color,background-color,transform] duration-300 ease-expo-out hover:-translate-y-0.5 hover:border-primary/45 hover:bg-white/[0.06] hover:text-foreground active:scale-95';

// The address breaks after the @ when it has to (a 320px phone), never in the
// middle of a word.
const [EMAIL_LOCAL, EMAIL_DOMAIN] = SUPPORT_EMAIL.split('@');

export function Footer() {
  const { t, i18n } = useTranslation();
  // Avatars, as in the navbar: only where the server says they are on.
  const avatarEntry = useAvatarEntry();
  const reduceMotion = useReducedMotion();
  const footerRef = useRef<HTMLElement>(null);
  // The signature rises from the moment the footer's top enters the screen
  // until its bottom reaches the screen's bottom. Measured by hand against
  // the window's scroll (2026-09-24): useScroll({ target }) warned on every
  // load that <html> is statically positioned, and making it relative would
  // move every absolutely placed popover that measures from the page.
  const { scrollY } = useScroll();
  const range = useMotionValue<[number, number]>([0, 1]);
  const progress = useTransform(() => {
    const [from, to] = range.get();
    return Math.min(1, Math.max(0, (scrollY.get() - from) / (to - from)));
  });
  const signatureY = useTransform(progress, [0, 1], ['38%', '0%']);
  const signatureOpacity = useTransform(progress, [0.2, 1], [0.2, 1]);

  useEffect(() => {
    const el = footerRef.current;
    if (!el || typeof window === 'undefined') return undefined;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      const from = top - window.innerHeight;
      const to = top + el.offsetHeight - window.innerHeight;
      const [oldFrom, oldTo] = range.get();
      const next: [number, number] = [from, Math.max(from + 1, to)];
      if (next[0] !== oldFrom || next[1] !== oldTo) range.set(next);
    };
    measure();
    // The page above grows and shrinks (fonts, media, the tool stops), which
    // moves the footer without a window resize.
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(document.body);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [range]);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  return (
    <footer
      ref={footerRef}
      className="relative overflow-hidden border-t border-white/[0.07] bg-[linear-gradient(180deg,rgba(8,10,14,0.55),rgba(5,6,9,0.98)_55%)]"
    >
      {/* Light along the top edge and a faint warm bloom under it. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[linear-gradient(90deg,transparent,hsl(40_88%_64%/0.45),hsl(184_52%_56%/0.35),transparent)]" />
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-64 w-[min(56rem,120vw)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,hsl(40_88%_64%/0.08),transparent)]" />

      {/* max-w-6xl inside the container (2026-09-24): the same content edge
          as the landing's tool cards, trust row and FAQ above it, instead of
          running 70px wider on each side on a desktop. */}
      {/* md:px-8 xl:px-4: the gutters the sections above use. */}
      <div className="container relative mx-auto px-4 pt-14 sm:pt-20 md:px-8 xl:px-4">
        <div className="mx-auto max-w-6xl">
          {/* Phones: the legal column gets a little more of the row
              (2026-09-25), so "Política de reembolsos" stays on one line at
              360 and the two columns' rows stay level. */}
          <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-x-5 gap-y-12 sm:grid-cols-2 md:grid-cols-12 md:gap-x-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-6 lg:col-span-5">
              {/* min-h in px: the mark's rem height is 41px on the 15px phone root. */}
              <Link to="/" className="group inline-flex min-h-[44px] items-center rounded-2xl">
                <BrandMark alt={t('navigation.logoAlt')} size="footer" />
              </Link>
              {/* 15px floor on phones (0.95rem is 14.25px on the 15px root). */}
              <p className="text-pretty mt-6 max-w-sm text-[15px] leading-relaxed text-muted-foreground sm:text-[0.95rem]">
                {t('footer.description')}
              </p>
              <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-muted-foreground sm:text-[0.9rem]">
                {t('footer.contactLine')}{' '}
                <a
                  href={SUPPORT_MAILTO}
                  className="-my-3 inline-block py-3 font-medium text-foreground underline decoration-white/25 underline-offset-4 transition-colors hover:decoration-primary"
                >
                  <span className="whitespace-nowrap">{EMAIL_LOCAL}@</span>
                  <wbr />
                  <span className="whitespace-nowrap">{EMAIL_DOMAIN}</span>
                </a>
              </p>
              <div className="mt-6 flex items-center gap-3">
                <a
                  href="https://www.facebook.com/share/17n7HpD2e8/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={ICON_CLASS}
                  title={t('footer.facebookTitle')}
                  aria-label={t('footer.facebookAria')}
                >
                  <Facebook className="h-[18px] w-[18px]" aria-hidden="true" />
                </a>
                <a href={SUPPORT_MAILTO} className={ICON_CLASS} aria-label={t('footer.emailAria')}>
                  <Mail className="h-[18px] w-[18px]" aria-hidden="true" />
                </a>
              </div>
            </div>

            {/* Product Links */}
            <nav aria-labelledby="footer-product" className="md:col-span-3 lg:col-span-2 lg:col-start-8">
              <h3 id="footer-product" className="font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/70">
                {t('footer.product')}
              </h3>
              {/* Router links, like the navbar's FAQ (2026-09-25): a plain
                  <a> reloaded the whole app from every other page, and on
                  the landing page left the jump to the browser's own smooth
                  scroll, which the ScrollTriggers' refresh cut short. Index
                  lines the section up either way now. */}
              {/* Every studio, from the one list (lib/studios.ts), then the
                  places in the frame (Studio Blueprint 02). */}
              <ul className="mt-3 md:mt-4 md:space-y-1">
                {STUDIOS.filter((s) => s.id !== 'editor').map((s) => (
                  <li key={s.id}>
                    <Link to={s.path(i18n.language)} className={LINK_CLASS} data-testid={`footer-studio-${s.id}`}>
                      {t(`studios.${s.key}.name`)}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/storybook/gift" className={LINK_CLASS} data-testid="footer-storybook-gift">
                    {t('storybookGift.entry.footer')}
                  </Link>
                </li>
                <li>
                  <Link to="/watch" className={LINK_CLASS}>
                    {t('shell.tabs.watch')}
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className={LINK_CLASS}>
                    {t('footer.pricing')}
                  </Link>
                </li>
                <li>
                  <Link to={{ pathname: '/', hash: '#faq' }} className={LINK_CLASS}>
                    {t('footer.faq')}
                  </Link>
                </li>
                {avatarEntry.show && (
                  <li>
                    <Link to={avatarEntry.href} className={LINK_CLASS} data-testid="footer-avatars">
                      {t('navigation.avatars')}
                    </Link>
                  </li>
                )}
                <li>
                  <Link to={editorPath('hub', editorLang(i18n.language))} className={LINK_CLASS}>
                    {t('footer.freeEditor')}
                  </Link>
                </li>
                <li>
                  <Link to={i18n.language?.startsWith('es') ? '/guides/es' : '/guides'} className={LINK_CLASS} data-testid="footer-guides">
                    {t('footer.guides')}
                  </Link>
                </li>
                {quickTools.order.map((key) => (
                  <li key={key}>
                    <Link
                      to={quickTools.tools[key as keyof typeof quickTools.tools].path}
                      className={LINK_CLASS}
                      data-testid={`footer-quick-${key}`}
                    >
                      {t(`qt.tools.${key}.name`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Legal Links */}
            <nav aria-labelledby="footer-legal" className="md:col-span-3 lg:col-span-2">
              <h3 id="footer-legal" className="font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/70">
                {t('footer.legal')}
              </h3>
              <ul className="mt-3 md:mt-4 md:space-y-1">
                <li>
                  <Link to="/privacy" className={LINK_CLASS}>
                    {t('footer.privacyPolicy')}
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className={LINK_CLASS}>
                    {t('footer.termsOfService')}
                  </Link>
                </li>
                <li>
                  <Link to="/refunds" className={LINK_CLASS}>
                    {t('footer.refundPolicy')}
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className={LINK_CLASS}>
                    {t('footer.contact')}
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          <div className="mt-14 flex flex-col-reverse items-start gap-5 border-t border-white/[0.07] pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[0.8rem] text-muted-foreground/80">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </p>
            <button
              type="button"
              onClick={toTop}
              className="group inline-flex h-[44px] items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] pl-4 pr-1.5 text-[0.82rem] font-medium text-muted-foreground transition-[color,border-color,background-color] duration-300 hover:border-white/20 hover:bg-white/[0.06] hover:text-foreground"
            >
              {t('immersiveChrome.footer.backToTop', { defaultValue: 'Back to top' })}
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/[0.06] transition-transform duration-500 ease-expo-out group-hover:-translate-y-0.5">
                <ArrowUp className="h-4 w-4" aria-hidden="true" />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Signature wordmark. An SVG so the word always spans the container
          exactly (textLength), from a 320px phone to a 4K screen, with no
          font-size maths and no sideways overflow. Its lower part is cut by
          the footer's bottom edge on purpose. */}
      <div aria-hidden="true" className="pointer-events-none relative mx-auto mt-6 max-w-[1400px] select-none px-3 sm:px-6">
        <motion.svg
          viewBox="0 0 1000 150"
          className="-mb-[2.5%] block h-auto w-full"
          style={reduceMotion ? undefined : { y: signatureY, opacity: signatureOpacity }}
        >
          <defs>
            {/* Fill only: Syne's variable outlines overlap inside letters
                like A and H, and a stroke would draw those seams. */}
            <linearGradient id="footer-signature-fill" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#F4BE52" stopOpacity="0.16" />
              <stop offset="45%" stopColor="#ffffff" stopOpacity="0.09" />
              <stop offset="100%" stopColor="#4FC5CD" stopOpacity="0.1" />
            </linearGradient>
          </defs>
          <text
            x="500"
            y="140"
            textAnchor="middle"
            textLength="996"
            lengthAdjust="spacingAndGlyphs"
            fontFamily="Syne, system-ui, sans-serif"
            fontWeight={700}
            fontSize="176"
            fill="url(#footer-signature-fill)"
          >
            FACESHOT
          </text>
        </motion.svg>
      </div>
    </footer>
  );
}
